import { ShieldAlert, ShieldCheck, UsersRound } from 'lucide-react';
import { Button } from './Button';

/**
 * Three states, all text-plus-icon, never colour alone (spec §6).
 *
 * Copy voice for refusals: state the rule, name the consequence, offer the fix.
 * The words "violation", "prohibited", "policy" and "compliance" never appear in
 * user-facing strings — including the name of this component in the UI.
 */

export function ComplianceSatisfied({ adultCount }: { adultCount: number }) {
  return (
    <span
      className="inline-flex items-center gap-2 t-caption"
      style={{ color: 'var(--color-status-compliant)' }}
    >
      <ShieldCheck size={16} aria-hidden="true" />
      <span className="tabular">
        {adultCount} adults present
      </span>
    </span>
  );
}

/** Adult-only threads: the rules genuinely do not apply, and saying so is honest. */
export function ComplianceNotApplicable() {
  return (
    <span
      className="inline-flex items-center gap-2 t-caption"
      style={{ color: 'var(--color-text-secondary)' }}
    >
      <UsersRound size={16} aria-hidden="true" />
      Adults only &mdash; youth protection rules don&rsquo;t apply
    </span>
  );
}

interface RefusalProps {
  /** Names what cannot happen. Not "Action blocked" — the specific thing. */
  headline: string;
  /** One sentence: the rule, then the consequence. */
  explanation: string;
  fixLabel: string;
  onFix?: () => void;
}

/**
 * Rendered inline in the stream, above the composer, when an action is blocked.
 * Never a toast, never a silent failure, never a disabled button on its own
 * (spec §10).
 */
export function RefusalCard({ headline, explanation, fixLabel, onFix }: RefusalProps) {
  return (
    <div
      role="alert"
      style={{
        background: 'var(--color-status-blocked-surface)',
        border: '1px solid var(--color-status-blocked)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4)',
        display: 'flex',
        gap: 'var(--space-3)',
        alignItems: 'flex-start',
      }}
    >
      <ShieldAlert
        size={20}
        aria-hidden="true"
        style={{ color: 'var(--color-status-blocked)', flexShrink: 0, marginTop: '2px' }}
      />

      <div className="flex flex-col gap-2 min-w-0">
        <p className="t-body-strong" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
          {headline}
        </p>
        <p className="t-body" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
          {explanation}
        </p>
        <div className="mt-1">
          <Button variant="secondary" onClick={onFix}>
            {fixLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
